// About page — hero, overview, market, offerings, process.  Config-driven:
// each component reads a `cfg` object (injected per-section from Shopify settings)
// and falls back to the original design content when a value is blank.
function useMob(bp = 768) {
  const [m, setM] = React.useState(() => window.matchMedia(`(max-width:${bp}px)`).matches);
  React.useEffect(() => {
    const q = window.matchMedia(`(max-width:${bp}px)`);
    const f = e => setM(e.matches);
    q.addEventListener('change', f);
    return () => q.removeEventListener('change', f);
  }, [bp]);
  return m;
}
const PAD = 'clamp(20px,5vw,80px)';
// text → {__html} with newlines as <br> (lets client add line breaks in a textarea)
function H(s) {
  return {
    __html: (s == null ? '' : String(s)).replace(/\r?\n/g, '<br>')
  };
}
// pick a value, falling back to default when blank/undefined
function P(v, d) {
  return v === undefined || v === null || v === '' ? d : v;
}
window.CravyAbout = window.CravyAbout || {};

// ---- MARQUEE (thin wrapper mapping section settings onto the shared Marquee) ----
function AboutMarquee({
  cfg
}) {
  const c = cfg || {};
  return /*#__PURE__*/React.createElement(Marquee, {
    text: P(c.text, 'Just fry it'),
    speed: Number(P(c.speed, 22)),
    bg: P(c.bg, '#000'),
    color: P(c.color, 'var(--cravy-yellow)')
  });
}
window.CravyAbout.marquee = AboutMarquee;

// ---- HERO: giant statement over fries photo ----
function AboutHero({
  cfg
}) {
  const c = cfg || {};
  const m = useMob();
  const jump = href => e => {
    if (href && href.charAt(0) === '#') {
      const t = document.getElementById(href.slice(1));
      if (t) {
        e.preventDefault();
        window.scrollTo({
          top: t.getBoundingClientRect().top + window.scrollY,
          behavior: 'smooth'
        });
      }
    }
  };
  const bg = P(c.bg, 'var(--cravy-red)'),
    text = P(c.text, '#fff'),
    accent = P(c.accent, 'var(--cravy-yellow)');
  const b1 = P(c.btn1_link, '#process'),
    b2 = P(c.btn2_link, '#inquiry');
  return /*#__PURE__*/React.createElement("section", {
    id: "about-hero",
    "data-screen-label": "About hero",
    style: {
      position: 'relative',
      minHeight: m ? '92svh' : '100vh',
      background: bg,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end'
    }
  }, P(c.img, '') ? /*#__PURE__*/React.createElement("img", {
    src: c.img,
    alt: "",
    style: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      opacity: Number(P(c.img_opacity, 0.32))
    }
  }) : null, /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0) 60%)'
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      padding: `0 ${PAD} clamp(48px,9vh,110px)`,
      display: 'flex',
      flexDirection: 'column',
      gap: 'clamp(14px,2vh,26px)'
    }
  }, /*#__PURE__*/React.createElement("h1", {
    className: "fadein-1",
    style: {
      color: text,
      textShadow: '0 2px 24px rgba(0,0,0,0.35)'
    },
    dangerouslySetInnerHTML: H(P(c.heading, 'We are\nCravy'))
  }), /*#__PURE__*/React.createElement("p", {
    className: "fadein-2",
    style: {
      margin: 0,
      maxWidth: 620,
      fontFamily: 'var(--font-body)',
      fontSize: 'clamp(16px,1.35vw,21px)',
      lineHeight: 1.35,
      color: text
    },
    dangerouslySetInnerHTML: H(P(c.body, 'Elevating menus with consistent quality and exceptional taste. Crispy, golden vibes in every fry made at a high-tech facility for restaurants, hotels, and everyone in between.'))
  }), /*#__PURE__*/React.createElement("div", {
    className: "fadein-3",
    style: {
      display: 'flex',
      gap: 14,
      flexWrap: 'wrap',
      marginTop: 6
    }
  }, /*#__PURE__*/React.createElement("a", {
    className: "herobtn herobtn-lite",
    href: b1,
    onClick: jump(b1),
    style: {
      textDecoration: 'none',
      fontFamily: 'var(--font-body)',
      fontWeight: 700,
      fontSize: 15,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      padding: '15px 30px',
      background: '#fff',
      color: '#000',
      borderRadius: 999
    }
  }, P(c.btn1_text, 'How we make them')), /*#__PURE__*/React.createElement("a", {
    className: "herobtn",
    href: b2,
    onClick: jump(b2),
    style: {
      textDecoration: 'none',
      fontFamily: 'var(--font-body)',
      fontWeight: 700,
      fontSize: 15,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      padding: '15px 30px',
      background: accent,
      color: '#000',
      borderRadius: 999
    }
  }, P(c.btn2_text, 'Become a partner')))));
}
window.CravyAbout.hero = AboutHero;

// ---- COMPANY OVERVIEW ----
function Overview({
  cfg
}) {
  const c = cfg || {};
  const m = useMob();
  const bg = P(c.bg, '#fff'),
    accent = P(c.accent, 'var(--cravy-red)'),
    text = P(c.text, '#000');
  const stats = [[P(c.stat1_k, '24/7'), P(c.stat1_v, 'Batch testing')], [P(c.stat2_k, 'ISO'), P(c.stat2_v, '9001 &amp; 22000')], [P(c.stat3_k, '100%'), P(c.stat3_v, 'Halal traced')]];
  return /*#__PURE__*/React.createElement("section", {
    "data-screen-label": "Company overview",
    style: {
      background: bg,
      padding: `clamp(60px,11vh,140px) ${PAD}`,
      display: 'grid',
      gridTemplateColumns: m ? '1fr' : '1.05fr 0.95fr',
      gap: 'clamp(30px,5vw,80px)',
      alignItems: 'center'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      flexDirection: 'column',
      gap: 22
    }
  }, /*#__PURE__*/React.createElement(Reveal, {
    as: "h2",
    delay: 80,
    style: {
      color: text
    },
    dangerouslySetInnerHTML: H(P(c.heading, 'Just fry it'))
  }), /*#__PURE__*/React.createElement(Reveal, {
    as: "p",
    delay: 140,
    style: {
      margin: 0,
      fontFamily: 'var(--font-body)',
      fontSize: 'clamp(16px,1.3vw,20px)',
      lineHeight: 1.45,
      color: '#222',
      maxWidth: 620,
      textWrap: 'pretty'
    },
    dangerouslySetInnerHTML: H(P(c.body, 'At Cravy Fries in Lahore, Punjab, we’re serving up crispy, golden vibes in every fry. With our high-tech facility and a focus on quality, we make sure every batch is top-notch for restaurants, fast food joints, hotels and masses. We’re all about that next-level crunch, reliability, and flavor. Let Cravy Fries add the sizzle your menu’s been missing.'))
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'grid',
      gridTemplateColumns: m ? '1fr 1fr' : 'repeat(3,auto)',
      gap: 'clamp(16px,2.5vw,44px)',
      marginTop: 8,
      justifyContent: 'start'
    }
  }, stats.map(([k, v], i) => /*#__PURE__*/React.createElement(Reveal, {
    key: i,
    delay: 200 + i * 70,
    style: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: 'var(--font-display)',
      fontSize: 'clamp(30px,3vw,46px)',
      lineHeight: 1,
      color: accent
    },
    dangerouslySetInnerHTML: H(k)
  }), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: 'var(--font-body)',
      fontWeight: 600,
      fontSize: 13.5,
      color: '#444'
    },
    dangerouslySetInnerHTML: H(v)
  }))))), /*#__PURE__*/React.createElement(Reveal, {
    kind: "tiltr",
    style: {
      borderRadius: 26,
      overflow: 'hidden',
      aspectRatio: m ? '4/3' : '4/5',
      boxShadow: '0 26px 60px rgba(0,0,0,0.18)'
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: P(c.img, ''),
    alt: "Cravy Fries facility",
    style: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block'
    }
  })));
}
window.CravyAbout.overview = Overview;

// ---- TARGET MARKET ----
function Market({
  cfg
}) {
  const c = cfg || {};
  const m = useMob();
  const [act, setAct] = React.useState(0);
  const bg = P(c.bg, '#000'),
    text = P(c.text, '#fff'),
    accent = P(c.accent, 'var(--cravy-yellow)');
  const segs = [{
    l: P(c.seg1_label, 'Restaurants'),
    img: P(c.seg1_img, '')
  }, {
    l: P(c.seg2_label, 'Cafés'),
    img: P(c.seg2_img, '')
  }, {
    l: P(c.seg3_label, 'Catering'),
    img: P(c.seg3_img, '')
  }, {
    l: P(c.seg4_label, 'Hotels'),
    img: P(c.seg4_img, '')
  }, {
    l: P(c.seg5_label, 'Fast food'),
    img: P(c.seg5_img, '')
  }];
  return /*#__PURE__*/React.createElement("section", {
    "data-screen-label": "Target market",
    style: {
      background: bg,
      color: text,
      padding: `clamp(60px,11vh,140px) ${PAD}`,
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement(Reveal, {
    as: "h2",
    delay: 80,
    style: {
      color: text,
      marginTop: 20
    },
    dangerouslySetInnerHTML: H(P(c.heading, 'Fries for\nevery table'))
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'grid',
      gridTemplateColumns: m ? '1fr' : '0.85fr 1.15fr',
      gap: 'clamp(26px,4vw,64px)',
      marginTop: 'clamp(30px,5vh,56px)',
      alignItems: 'stretch'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      flexDirection: 'column',
      gap: 18
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 10
    }
  }, segs.map((s, i) => /*#__PURE__*/React.createElement("span", {
    key: i,
    onMouseEnter: () => setAct(i),
    onClick: () => setAct(i),
    className: "chip",
    style: {
      cursor: 'pointer',
      fontFamily: 'var(--font-body)',
      fontWeight: 700,
      fontSize: 'clamp(13px,1vw,15px)',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      padding: '13px 24px',
      border: '2px solid ' + (act === i ? accent : text),
      background: act === i ? accent : 'transparent',
      color: act === i ? '#000' : text,
      borderRadius: 999
    },
    dangerouslySetInnerHTML: H(s.l)
  }))), /*#__PURE__*/React.createElement(Reveal, {
    as: "p",
    style: {
      margin: 0,
      fontFamily: 'var(--font-body)',
      fontSize: 'clamp(16px,1.25vw,19px)',
      lineHeight: 1.45,
      color: 'rgba(255,255,255,0.82)',
      textWrap: 'pretty'
    },
    dangerouslySetInnerHTML: H(P(c.body, 'We get that everyone craves great fries — whether you’re running a restaurant or just craving a snack at home. As we level up in Pakistan, we’re aiming to spread the love globally, bringing our crispy goodness to kitchens and tables worldwide.'))
  }), /*#__PURE__*/React.createElement("span", {
    style: {
      marginTop: 'auto',
      fontFamily: 'var(--font-body)',
      fontSize: 12.5,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.4)'
    }
  }, "Hover a segment →")), /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      borderRadius: 26,
      overflow: 'hidden',
      aspectRatio: m ? '4/3' : '16/10',
      minHeight: 220
    }
  }, segs.map((s, i) => /*#__PURE__*/React.createElement("img", {
    key: i,
    src: s.img,
    alt: "",
    style: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      opacity: act === i ? 1 : 0,
      transform: act === i ? 'scale(1)' : 'scale(1.05)',
      transition: 'opacity .55s cubic-bezier(.22,.9,.3,1), transform .8s cubic-bezier(.22,.9,.3,1)'
    }
  })), /*#__PURE__*/React.createElement("span", {
    style: {
      position: 'absolute',
      left: 18,
      bottom: 18,
      fontFamily: 'var(--font-display)',
      fontSize: 'clamp(20px,2vw,30px)',
      textTransform: 'uppercase',
      color: '#fff',
      textShadow: '0 2px 14px rgba(0,0,0,0.6)'
    },
    dangerouslySetInnerHTML: H(segs[act].l)
  }))));
}
window.CravyAbout.market = Market;

// ---- PRODUCT OFFERINGS ----
function Offerings({
  cfg
}) {
  const c = cfg || {};
  const m = useMob();
  const [act, setAct] = React.useState(0);
  const bg = P(c.bg, 'var(--cravy-yellow)'),
    accent = P(c.accent, 'var(--cravy-red)'),
    label = P(c.label_color, 'var(--cravy-maroon)');
  const offs = [{
    k: P(c.off1_label, 'Varieties'),
    v: P(c.off1_value, 'Lady Rosetta &amp; Alverstone Russet'),
    n: '01',
    img: P(c.off1_img, '')
  }, {
    k: P(c.off2_label, 'Cut'),
    v: P(c.off2_value, 'Straight cut — 6mm, 7mm, 9mm, 11mm'),
    n: '02',
    img: P(c.off2_img, '')
  }, {
    k: P(c.off3_label, 'Packaging'),
    v: P(c.off3_value, '2kg &amp; 2.5kg retail-ready bags'),
    n: '03',
    img: P(c.off3_img, '')
  }, {
    k: P(c.off4_label, 'Sourcing'),
    v: P(c.off4_value, 'Freshly harvested, locally grown'),
    n: '04',
    img: P(c.off4_img, '')
  }];
  const isPng = u => typeof u === 'string' && u.split('?')[0].toLowerCase().endsWith('.png');
  return /*#__PURE__*/React.createElement("section", {
    "data-screen-label": "Cravy offerings",
    style: {
      background: bg,
      color: '#000',
      padding: `clamp(60px,11vh,140px) ${PAD}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'grid',
      gridTemplateColumns: m ? '1fr' : '1.1fr 0.9fr',
      gap: 'clamp(28px,5vw,70px)',
      alignItems: 'start'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      flexDirection: 'column'
    }
  }, /*#__PURE__*/React.createElement(Reveal, {
    as: "h2",
    delay: 80,
    style: {
      marginTop: 18,
      marginBottom: 26
    },
    dangerouslySetInnerHTML: H(P(c.heading, 'Cravy\nofferings'))
  }), offs.map((o, i) => /*#__PURE__*/React.createElement("div", {
    key: i,
    onMouseEnter: () => setAct(i),
    onClick: () => setAct(i),
    style: {
      display: 'grid',
      gridTemplateColumns: m ? '40px 1fr' : '72px 1fr',
      gap: m ? 14 : 22,
      alignItems: 'baseline',
      padding: 'clamp(18px,2.6vh,30px) ' + (act === i ? '14px' : '0'),
      borderTop: '2px solid rgba(0,0,0,0.22)',
      background: act === i ? 'rgba(255,255,255,0.55)' : 'transparent',
      cursor: 'pointer',
      transition: 'background .3s, padding .3s'
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: 'var(--font-display)',
      fontSize: 'clamp(22px,2vw,34px)',
      lineHeight: 1,
      color: act === i ? accent : 'rgba(65,18,21,0.4)',
      transition: 'color .3s'
    }
  }, o.n), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: 'var(--font-body)',
      fontWeight: 700,
      fontSize: 12.5,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      color: label
    },
    dangerouslySetInnerHTML: H(o.k)
  }), /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: 'var(--font-body)',
      fontWeight: 600,
      fontSize: 'clamp(19px,2.1vw,32px)',
      lineHeight: 1.12,
      letterSpacing: '-0.01em'
    },
    dangerouslySetInnerHTML: H(o.v)
  })))), /*#__PURE__*/React.createElement("div", {
    style: {
      borderTop: '2px solid rgba(0,0,0,0.22)'
    }
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      borderRadius: 24,
      overflow: 'hidden',
      aspectRatio: m ? '4/3' : '3/4',
      background: 'rgba(0,0,0,0.06)',
      position: m ? 'relative' : 'sticky',
      top: m ? 'auto' : 'clamp(40px,10vh,110px)'
    }
  }, offs.map((o, i) => /*#__PURE__*/React.createElement("img", {
    key: i,
    src: o.img,
    alt: "",
    style: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: isPng(o.img) ? 'contain' : 'cover',
      padding: isPng(o.img) ? '8%' : 0,
      boxSizing: 'border-box',
      opacity: act === i ? 1 : 0,
      transform: act === i ? 'scale(1)' : 'scale(1.04)',
      transition: 'opacity .55s cubic-bezier(.22,.9,.3,1), transform .8s cubic-bezier(.22,.9,.3,1)'
    }
  })))));
}
window.CravyAbout.offerings = Offerings;

// ---- MANUFACTURING PROCESS ----
function Process({
  cfg
}) {
  const c = cfg || {};
  const m = useMob();
  const bg = P(c.bg, 'var(--cravy-white)'),
    accent = P(c.accent, 'var(--cravy-red)');
  const steps = [[P(c.step1_title, 'Sorting &amp; washing'), P(c.step1_body, 'Fresh potatoes are thoroughly cleaned and sorted for quality.')], [P(c.step2_title, 'Peeling'), P(c.step2_body, 'The potatoes are peeled to remove their outer skin.')], [P(c.step3_title, 'Cutting'), P(c.step3_body, 'Peeled potatoes are cut into uniform fry shapes.')], [P(c.step4_title, 'Blanching'), P(c.step4_body, 'Processed through hot water for starch removal, preserving color and texture.')], [P(c.step5_title, 'Frying'), P(c.step5_body, 'The fries are partially cooked in hot oil for a crispy texture.')], [P(c.step6_title, 'Drying'), P(c.step6_body, 'Excess moisture is removed for a consistent, quality product.')], [P(c.step7_title, 'Freezing'), P(c.step7_body, 'The fries are quickly frozen to lock in freshness and quality.')]];
  return /*#__PURE__*/React.createElement("section", {
    id: "process",
    "data-screen-label": "Manufacturing process",
    style: {
      background: bg,
      color: '#000',
      padding: `clamp(60px,11vh,140px) 0`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      padding: `0 ${PAD}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: 16,
      alignItems: 'start',
      marginTop: 20,
      marginBottom: 'clamp(30px,5vh,56px)'
    }
  }, /*#__PURE__*/React.createElement(Reveal, {
    as: "h2",
    delay: 80,
    dangerouslySetInnerHTML: H(P(c.heading, 'Field to freezer\nin seven steps'))
  }), /*#__PURE__*/React.createElement(Reveal, {
    as: "p",
    delay: 140,
    style: {
      margin: 0,
      maxWidth: 560,
      fontFamily: 'var(--font-body)',
      fontSize: 'clamp(15px,1.15vw,17.5px)',
      lineHeight: 1.45,
      color: '#444'
    },
    dangerouslySetInnerHTML: H(P(c.body, 'Every batch runs the same route through our Lahore plant — so the crunch never changes.'))
  }))), /*#__PURE__*/React.createElement("div", null, steps.map((s, i) => /*#__PURE__*/React.createElement(Reveal, {
    key: i,
    delay: 40,
    style: {
      background: i % 2 ? '#fff' : 'transparent',
      padding: `clamp(20px,3vh,34px) ${PAD}`
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'grid',
      gridTemplateColumns: m ? '52px 1fr' : '110px 0.9fr 1.1fr',
      gap: m ? 16 : 'clamp(20px,3vw,54px)',
      alignItems: m ? 'baseline' : 'center',
      maxWidth: 1500
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontFamily: 'var(--font-statement)',
      fontSize: 'clamp(34px,4vw,68px)',
      lineHeight: 0.8,
      letterSpacing: '-0.06em',
      color: accent
    }
  }, String(i + 1).padStart(2, '0')), /*#__PURE__*/React.createElement("h3", {
    style: {
      fontSize: 'clamp(22px,2.1vw,32px)'
    },
    dangerouslySetInnerHTML: H(s[0])
  }), /*#__PURE__*/React.createElement("p", {
    style: {
      margin: 0,
      gridColumn: m ? '2' : 'auto',
      fontFamily: 'var(--font-body)',
      fontSize: 'clamp(14.5px,1.1vw,17px)',
      lineHeight: 1.45,
      color: '#555'
    },
    dangerouslySetInnerHTML: H(s[1])
  }))))), /*#__PURE__*/React.createElement("div", {
    style: {
      padding: `clamp(30px,5vh,60px) ${PAD} 0`,
      display: 'grid',
      gridTemplateColumns: m ? '1fr' : '1fr 1fr',
      gap: 'clamp(14px,2vw,24px)'
    }
  }, /*#__PURE__*/React.createElement(Reveal, {
    style: {
      borderRadius: 22,
      overflow: 'hidden',
      aspectRatio: '16/10'
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: P(c.img1, ''),
    alt: "",
    style: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block'
    }
  })), /*#__PURE__*/React.createElement(Reveal, {
    delay: 100,
    kind: "tiltr",
    style: {
      borderRadius: 22,
      overflow: 'hidden',
      aspectRatio: '16/10'
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: P(c.img2, ''),
    alt: "",
    style: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block'
    }
  }))));
}
window.CravyAbout.process = Process;
Object.assign(window, {
  useMob,
  AboutHero,
  AboutMarquee,
  Overview,
  Market,
  Offerings,
  Process,
  PAD,
  H,
  P
});

